/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

/* eslint-disable no-duplicate-imports */
import {
    ICartlinesViewProps,
    ICartResources,
    ICartViewProps,
    IOrderSummaryErrors
} from '@msdyn365-commerce-modules/cart';
import { ICartLineItemViewProps } from '@msdyn365-commerce-modules/cart/dist/types/modules/cart/components/cart-line-items-promotion';
import { IInvoiceSummaryLines } from '@msdyn365-commerce-modules/invoice-payment-summary';
import { IOrderSummaryLines } from '@msdyn365-commerce-modules/order-summary-utilities';
import { Button, getPayloadObject, getTelemetryAttributes, INodeProps, ITelemetryContent, Node, TelemetryConstant } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import { ICartData } from '@msdyn365-commerce-modules/cart/src/modules/cart/./cart.data';
import { ICartProps } from '../definition-extensions/cart.ext.props.autogenerated';

interface ICartTitle {
    props: {
        children: string
    };
}

const CartView: React.FC<ICartViewProps & ICartProps<ICartData>> = (props: ICartViewProps & ICartProps<ICartData>) => {

    props.config.cartTitle && ((props.title as ICartTitle).props.children = props.config.cartTitle.text);

    let orderSummaryWrapper: INodeProps = {...props.OrderSummaryWrapper};
    let isGuest: boolean = false;
    if(props.checkoutAsGuestButton){
        orderSummaryWrapper.className += ' guest-checkout-flow';
        isGuest = true
    }

    return (
        <div className={props.className} id={props.id} {...props.renderModuleAttributes(props)}>
            {props.title}
            {props.promotionOptions && _renderPromotions(props.promotionOptions)}
            {props.multiplePickUpEnabled ? _renderCartLinesGroup(props, props.resources) : <Node {...props.CartlinesWrapper}>
                {_renderCartlines(props.cartlines, props.resources, props.storeSelector, props.backToShoppingButton, props.waitingComponent, props.cartLoadingStatus,
                    props.cartDataResult, props.telemetryContent, props.multiplePickUpEnabled, props.context.actionContext.requestContext.channel?.EmailDeliveryModeCode)}
            </Node>}
            {props.orderSummaryHeading &&
                <Node {...orderSummaryWrapper}>
                    {props.orderSummaryHeading}
                    {props.cart?.hasInvoiceLine ? _renderInvoiceSummarylines(props.invoiceSummaryLineitems, props.OrderSummaryItems, props) : _renderOrderSummarylines(props.orderSummaryLineitems, props.OrderSummaryItems, props)}
                    {_renderErrorBlock(props.OrderSummaryErrors)}
                    {_renderPrivacyPolicy(isGuest, props)}
                    {_renderTermsAndConditions(isGuest, props)}
                    {props.checkoutAsGuestButton}
                    {props.checkoutAsSignInUserButton}
                    {props.backToShoppingButton}
                    {props.createTemplateFromCartButton}
                </Node>}
            {props.storeSelector}
        </div>
    );
};

const _renderPrivacyPolicy = (isGuest: boolean, props: ICartViewProps & ICartProps<ICartData>): JSX.Element | undefined => {
    if (!isGuest)
        return undefined

        const channelNaturalId: string | undefined = props.context.request.channel?.ChannelNaturalId;
        debugger;
        switch (channelNaturalId) {
            case 'PimkieDE':
                return (
                    <p className='guest-advertise'>
                        Wir verarbeiten deine personenbezogenen Daten gemäß der <a href='https://www.pimkie.de/confidentialite' target='_blank'>Datenschutzerklärung</a> von Young Fashion E-Commerce GmBH.
                    </p>
                )
            case 'PimkieES':
                return (
                    <p className='guest-advertise'>
                        Procesaremos tus datos personales conforme a la <a href='https://www.pimkie.es/confidentialite' target='_blank'>Política de privacidad</a> de Young Fashion E-Commerce GmBH.
                    </p>
                )
            case 'PimkiePT':
                return (
                    <p className='guest-advertise'>
                        Vamos processar os seus dados pessoais de acordo com a <a href='https://www.pimkie.pt/confidentialite' target='_blank'>Política de Privacidade</a> da Young Fashion E-Commerce GmBH.
                    </p>
                )
            case 'PimkieAT':
                return (
                    <p className='guest-advertise'>
                        Wir verarbeiten deine personenbezogenen Daten gemäß der <a href='https://www.pimkie.at/confidentialite.html' target='_blank'>Datenschutzerklärung</a> von Young Fashion E-Commerce GmBH.
                    </p>
                )
            default:
                return <p className='guest-advertise'>
                    Procesaremos tus datos personales conforme a la <a href='https://www.pimkie.es/confidentialite.html' target='_blank'>Política de privacidad</a> de Young Fashion E-Commerce GmBH.
                </p>;
        }
}

const _renderTermsAndConditions = (isGuest: boolean, props: ICartViewProps & ICartProps<ICartData>): JSX.Element | undefined => {
    if (!isGuest)
        return undefined

        const channelNaturalId: string | undefined = props.context.request.channel?.ChannelNaturalId;
    
        switch (channelNaturalId) {
            case 'PimkieDE':
                return (
                    <p className='guest-advertise'>
                        Wenn du fortfährst, stimmst du damit den <a href='https://www.pimkie.de/cgv' target='_blank'>Allgemeinen Geschäftsbedingungen</a> von Young Fashion E-Commerce GmBH.
                    </p>
                )
            case 'PimkieES':
                return (
                    <p className='guest-advertise'>
                        Al continuar, aceptas los <a href='https://www.pimkie.es/cgv' target='_blank'>Términos y Condiciones generales</a> de Young Fashion E-Commerce GmBH.
                    </p>
                )
            case 'PimkiePT':
                return (
                    <p className='guest-advertise'>
                        Ao continuar, concorda com os <a href='https://www.pimkie.pt/cgv' target='_blank'>Termos e Condições Gerais</a> da Young Fashion E-Commerce GmBH.
                    </p>
                )
            case 'PimkieAT':
                return (
                    <p className='guest-advertise'>
                        Wenn du fortfährst, stimmst du damit den <a href='https://www.pimkie.at/cgv' target='_blank'>Allgemeinen Geschäftsbedingungen</a> von Young Fashion E-Commerce GmBH.
                    </p>
                )
            default:
                return (
                    <p className='guest-advertise'>
                        Al continuar, aceptas los <a href='https://www.pimkie.es/cgv' target='_blank'>Términos y Condiciones generales</a> de Young Fashion E-Commerce GmBH.
                    </p>
                )
        }
}

const _renderPromotions = (promotions: ICartLineItemViewProps): JSX.Element | undefined => {
    return (
        <>
            <Node {...promotions.promotionMessageWrapper} >
                {promotions.promotionMessage}
                {promotions.promotionSeeAllLink}
                {promotions.promotionDialog}
            </Node>

        </>
    );

};

const _renderCartlines = (cartLines: ICartlinesViewProps[] | undefined, resources: ICartResources, storeSelector: React.ReactNode | undefined,
    backToShoppingButton: React.ReactNode, waitingComponent: React.ReactNode, cartLoadingStatus: string, cartDataResult: boolean,
    telemetryContent?: ITelemetryContent, mulitplePickUp?: boolean, emailDeliveryModeCode?: string): JSX.Element[] | JSX.Element => {

    const { emptyCartText } = resources;

    if (cartLoadingStatus) {
        return (<>
            {cartLoadingStatus}
        </>);
    }
    if (cartLines) {
        return cartLines.map((cartLine, index) => {
            return (
                <div className='msc-cart-lines-item' key={index}>
                    {cartLine.cartline}
                    {mulitplePickUp ? null : _renderBOPISBlock(cartLine, resources, storeSelector, telemetryContent, emailDeliveryModeCode)}
                    {
                        mulitplePickUp ? (
                            <Node className='msc-cart-line-group__extra-actions'>
                                {_renderGroupBOPISBlock(cartLine, resources, storeSelector, telemetryContent, emailDeliveryModeCode)}
                                {cartLine.addToOrderTemplate}
                                {cartLine.addToWishlist}
                                {cartLine.remove}
                            </Node>
                        ) : (cartLine.addToOrderTemplate ? (
                            <Node className='msc-cart-line__extra-actions'>
                                {cartLine.addToOrderTemplate}
                                {cartLine.addToWishlist}
                                {cartLine.remove}
                            </Node>
                        ) : (
                            <>
                                {cartLine.addToWishlist}
                                {cartLine.remove}
                            </>
                        ))
                    }
                </div>
            );
        });
    }
    return (cartDataResult ? (
        <div className='msc-cart__empty-cart'>
            <p className='msc-cart-line'>
                {emptyCartText}
            </p>
            {backToShoppingButton}
        </div>
    ) : <>
        {waitingComponent}
    </>
    );

};

/**
 * On Toggle function.
 * @param cartLine -Cartline.
 * @param isBopisSelected -Boolean.
 * @returns Set state of button.
 */
const onToggleBopisHandler = (cartLine: ICartlinesViewProps, isBopisSelected: boolean) => () => {
    cartLine.pickUpInStore && cartLine.pickUpInStore.callbacks.toggleBopis(!isBopisSelected);
};

/**
 * On change store function.
 * @param cartLine -CartLine items prop.
 * @returns Set state of button.
 */
const onChangeStoreHandler = (cartLine: ICartlinesViewProps) => () => {
    cartLine.pickUpInStore && cartLine.pickUpInStore.callbacks.toggleBopis(true);
};

const _renderBOPISBlock = (cartLine: ICartlinesViewProps, resources: ICartResources, storeSelector: React.ReactNode | undefined, telemetryContent?: ITelemetryContent, emailDeliveryModeCode?: string): JSX.Element | null => {
    // If it is electronic item cart line, then return null for BOPISBlock.
    if (!cartLine.pickUpInStore || !storeSelector || cartLine.data?.cartline.DeliveryMode === emailDeliveryModeCode) {
        return null;
    }

    const { shipInsteadDisplayText, shipToAddressDisplayText, pickItUpDisplayText, pickUpAtStoreWithLocationText, changeStoreDisplayText } = resources;

    const isBopisSelected = cartLine.pickUpInStore.isBopisSelected;

    const payLoad = getPayloadObject('click', telemetryContent!, TelemetryConstant.PickupInStore);
    const puckUpinStoreAttribute = getTelemetryAttributes(telemetryContent!, payLoad);

    return (
        <Node {...cartLine.pickUpInStore.ContainerProps}>
            <div className='msc-cart-line__bopis-method'>
                {isBopisSelected ? <span className='pick-up'>
                    {pickUpAtStoreWithLocationText}
                </span> : <span className='ship'>
                    {shipToAddressDisplayText}
                </span>}
            </div>
            {
                isBopisSelected &&
                <div className='msc-cart-line__bopis-fullfilment'>
                    <span className='msc-cart-line__bopis-fullfilment-store'>
                        {cartLine.pickUpInStore.orgUnitName}
                    </span>
                    <Button
                        role='link' className='msc-cart-line__bopis-changestore'
                        onClick={onChangeStoreHandler(cartLine)}>
                        {changeStoreDisplayText}
                    </Button>
                </div>
            }
            <Button className='msc-cart-line__bopis-btn' {...puckUpinStoreAttribute} onClick={onToggleBopisHandler(cartLine, isBopisSelected)}>
                {isBopisSelected ? shipInsteadDisplayText : pickItUpDisplayText}
            </Button>
        </Node>
    );
};

const _renderErrorBlock = (errorData: IOrderSummaryErrors | undefined): JSX.Element | null => {
    if (!errorData || errorData.errors.length === 0) {
        return null;
    }
    return (
        <Node {...errorData.Wrapper}>
            {errorData.header}
            {errorData.errors}
        </Node>
    );
};

const _renderOrderSummarylines = (orderSummaryLines: IOrderSummaryLines | undefined, OrderSummaryItems: INodeProps, props: ICartViewProps): JSX.Element | null => {
    if (!orderSummaryLines) {
        return null;
    }
    return (
        <Node {...OrderSummaryItems}>
            {props.promoCode}
            {orderSummaryLines.subtotal}
            {orderSummaryLines.shipping}
            {orderSummaryLines.otherCharge}
            {orderSummaryLines.tax}
            {orderSummaryLines.totalDiscounts ? orderSummaryLines.totalDiscounts : null}
            {orderSummaryLines.orderTotal}
        </Node>
    );
};

const _renderInvoiceSummarylines = (invoiceSummaryLines: IInvoiceSummaryLines | undefined, OrderSummaryItems: INodeProps, props: ICartViewProps): JSX.Element | null => {
    if (!invoiceSummaryLines) {
        props.context.telemetry.error('InvoiceSummary content is empty, module wont render');
        return null;
    }
    return (
        <Node {...OrderSummaryItems}>
            {invoiceSummaryLines.invoices}
            {invoiceSummaryLines.giftCard}
            {invoiceSummaryLines.loyalty}
            {invoiceSummaryLines.orderTotal}
        </Node>
    );
};

const _renderCartLinesGroup = (props: ICartViewProps, resources: ICartResources): JSX.Element | undefined => {
    if (props.cartLinesGroup && props.cartLinesGroup.length > 0) {
        return (
            <div className='msc-cart-lines-group'>
                {props.cartLinesGroup.map(cartlines => {
                    return (<div className='msc-cart-lines-group-wraper'>
                        {_renderCartLinesGroupHeader(cartlines[0], props.storeSelector, resources, _countItems(cartlines), props.context.actionContext.requestContext.channel?.EmailDeliveryModeCode)}
                        {_renderCartlines(cartlines, props.resources, props.storeSelector, props.backToShoppingButton, props.waitingComponent, props.cartLoadingStatus, props.cartDataResult,
                            props.telemetryContent, props.multiplePickUpEnabled, props.context.actionContext.requestContext.channel?.EmailDeliveryModeCode)}
                    </div>);
                })}
            </div>
        );
    }
    return (props.cartDataResult ? (
        <div className='msc-cartline-wraper'>
            <div className='msc-cart__empty-cart'>
                <p className='msc-cart-line'>
                    {props.resources.emptyCartText}
                </p>
                {props.backToShoppingButton}
            </div>
        </div>
    ) : <>
        {props.waitingComponent}
    </>
    );

};
const _renderCartLinesGroupHeader = (cartLine: ICartlinesViewProps, storeSelector: React.ReactNode | undefined, resources: ICartResources, count: number, emailDeliveryModeCode?: string) => {
    const isBopisSelected = cartLine.pickUpInStore?.isBopisSelected;
    const { pickUpText, shippingText, emailshippingText } = resources;
    let groupTitle: string;

    groupTitle = isBopisSelected ? pickUpText : shippingText;
    groupTitle = cartLine.data && cartLine.data.cartline.DeliveryMode === emailDeliveryModeCode ? emailshippingText : groupTitle;

    return (
        <>
            <div className='msc-cart-lines-group-wraper__bopis-heading'>
                <p className={`msc-cart-lines-group-wraper__bopis-heading-${groupTitle.toLowerCase()}-icon`} />
                <p className={`msc-cart-lines-group-wraper__bopis-heading-${groupTitle.toLowerCase()}`}>
                    {groupTitle}
                </p>
                {_renderCartLinesGroupTitle(cartLine, resources, count)}
            </div>
        </>
    );
};

const _renderGroupBOPISBlock = (cartLine: ICartlinesViewProps, resources: ICartResources, storeSelector: React.ReactNode | undefined, telemetryContent?: ITelemetryContent, emailDeliveryModeCode?: string): JSX.Element | null => {
    // If it is electronic item cart line, then return null for GroupBOPISBlock.
    if (!cartLine.pickUpInStore || !storeSelector || cartLine.data?.cartline.DeliveryMode === emailDeliveryModeCode) {
        return null;
    }

    const { changeStoreDisplayText, shipInsteadDisplayText, pickItUpDisplayText } = resources;

    const isBopisSelected = cartLine.pickUpInStore.isBopisSelected;

    const payLoad = getPayloadObject('click', telemetryContent!, TelemetryConstant.PickupInStore);
    const puckUpinStoreAttribute = getTelemetryAttributes(telemetryContent!, payLoad);

    return (
        <>
            {isBopisSelected ? <Button className='msc-cart-line__remove-item msc-btn' onClick={onChangeStoreHandler(cartLine)}>
                {changeStoreDisplayText}
            </Button> : null}
            <Button
                className='msc-cart-line__remove-item msc-btn' {...puckUpinStoreAttribute}
                onClick={onToggleBopisHandler(cartLine, isBopisSelected)}>
                {isBopisSelected ? shipInsteadDisplayText : pickItUpDisplayText}
            </Button>
        </>
    );
};

const _countItems = (cartLine: ICartlinesViewProps[]): number => {
    let countItem = 0;
    countItem = cartLine.reduce((count, item) => {
        return count + (item.data?.cartline.Quantity || 0);

    }, 0);
    return countItem;
};

const _renderCartLinesGroupTitle = (cartLine: ICartlinesViewProps, resources: ICartResources, count: number) => {
    const isBopisSelected = cartLine.pickUpInStore?.isBopisSelected;
    const { itemLabel, itemsLabel } = resources;
    const suffix = count > 1 ? itemsLabel : itemLabel;
    return (
        <>
            <Node className='msc-cart-lines-group-wraper__bopis-heading-title'>
                {isBopisSelected ? <p className='msc-cart-lines-group-wraper__bopis-heading-title-st'>
                    {cartLine.pickUpInStore?.deliveryOption}
                    {', '}
                    {cartLine.pickUpInStore?.orgUnitName}
                </p> : null}
                <p className='msc-cart-lines-group-wraper__bopis-heading-title-ct'>
                    (
                    {count}
                    {' '}
                    {suffix}
                    )
                </p>
            </Node>
        </>
    );
};

export default CartView;
